import {
  Box,
  GDTControlFrame,
  GDTSymbolText,
  HStack,
  PopoverBody,
  Text,
  VStack,
} from '@frontend/components';

import { FindingProvenance } from './FindingProvenance';
import type { FindingData } from './types';

const shouldShowDescription = (label: string, description: string) =>
  description.trim() !== '' &&
  label.toLowerCase().trim() !== description.toLowerCase().trim();

const shouldShowProvenance = (
  finding: FindingData,
  currentPartFileUuid: string | null | undefined,
) =>
  finding.count > 1 ||
  !currentPartFileUuid ||
  finding.fileReferences.length !== 1 ||
  finding.fileReferences[0].partFileUuid !== currentPartFileUuid;

export type FindingContentProps = {
  finding: FindingData;
  /** Slot for top-right icon buttons (copy, create-rule icon, etc.) */
  headerActions?: React.ReactNode;
  /** Slot for body actions area (geometric set-as, part descriptor set-as, etc.) */
  actionSlot?: React.ReactNode;
  /** Slot for footer actions area (mark inaccurate, create rule, learn more, etc.) */
  footerSlot?: React.ReactNode;
  /** Additional content rendered between action slot and provenance */
  additionalContent?: React.ReactNode;
  showProvenance?: boolean;
  currentPartFileUuid?: string | null;
  asPopover?: boolean;
};

const BodyContent = ({
  finding,
  headerActions,
  actionSlot,
  additionalContent,
  currentPartFileUuid,
  showProvenance,
}: Pick<
  FindingContentProps,
  | 'finding'
  | 'headerActions'
  | 'actionSlot'
  | 'additionalContent'
  | 'currentPartFileUuid'
  | 'showProvenance'
>) => {
  const showDescription = shouldShowDescription(
    finding.label,
    finding.description,
  );

  return (
    <>
      <HStack
        px={1.5}
        py={1}
        spacing={0.25}
        justifyContent="start"
        alignItems="start"
      >
        <Box flexGrow={1}>
          <VStack align="start" spacing={0.25} gap={0}>
            <Box display="inline-block">
              {finding.controlFrameProps ? (
                <GDTControlFrame {...finding.controlFrameProps} size="md" />
              ) : finding.isGdt ? (
                <GDTSymbolText size="md">{finding.label}</GDTSymbolText>
              ) : (
                <Text size="md">{finding.label}</Text>
              )}
            </Box>
            {showDescription && (
              <Text size="sm" color="feature.secondary">
                {finding.description}
              </Text>
            )}
          </VStack>
        </Box>

        {headerActions}
      </HStack>
      {actionSlot}
      {additionalContent}
      {showProvenance && shouldShowProvenance(finding, currentPartFileUuid) && (
        <FindingProvenance fileReferences={finding.fileReferences} />
      )}
    </>
  );
};

export const FindingContent = ({
  asPopover = true,
  footerSlot,
  ...rest
}: FindingContentProps) => {
  const BodyWrapper = asPopover ? PopoverBody : Box;

  return (
    <>
      <BodyWrapper
        as={asPopover ? VStack : Box}
        alignItems="stretch"
        gap={0}
        p={0}
        overflow="clip"
      >
        <BodyContent {...rest} />
      </BodyWrapper>
      {footerSlot}
    </>
  );
};
